/**
 * Band-Bibel: die Kategorien eines Projekts.
 *
 * Aufbau:
 *   Projekt → Band-Bibel → Kategorie → (eigene Räume) → Fläche
 *
 * Die vier Kategorien Allgemein, Gigs, Songs und Sonstiges sind das
 * Grundgerüst. Sie lassen sich weder umbenennen noch löschen – sonst löste
 * man versehentlich die Ablage der ganzen Band auf. Eigene Räume legt man
 * DARIN an, und die darf man mit allem tun.
 */
import type { View } from '../app.ts';
import { navigate, refresh } from '../app.ts';
import type { Room } from '../types.ts';
import { activeProject } from '../db/active.ts';
import {
  childRooms,
  deleteRoom,
  emptyRoom,
  ensureRooms,
  listProjectBoards,
  saveRoom,
  systemRooms,
} from '../db/repo.ts';
import { button, h } from '../ui/dom.ts';
import { toast } from '../ui/toast.ts';
import { askText } from '../ui/ask.ts';

/** "3 Notizen" oder "Noch leer". */
function countLabel(n: number): string {
  if (n === 0) return 'Noch leer';
  return `${n} ${n === 1 ? 'Fläche' : 'Flächen'}`;
}

export async function renderBible(): Promise<View> {
  const project = await activeProject();
  if (!project) {
    navigate('#/projects', true);
    return { title: 'Band-Bibel', body: h('div'), drawing: null };
  }
  const projectId = project.id;

  const rooms = await ensureRooms(projectId);
  const boards = await listProjectBoards(projectId);
  const categories = systemRooms(rooms);

  /** Wie viele Flächen liegen in diesem Raum und allem darunter? */
  function boardsIn(roomId: string): number {
    const direct = boards.filter((b) => b.roomId === roomId).length;
    const nested = childRooms(rooms, roomId).reduce((sum, r) => sum + boardsIn(r.id), 0);
    return direct + nested;
  }

  async function addRoom(parent: Room): Promise<void> {
    const name = await askText({ title: 'Neuer Raum', label: `Name des Raums in „${parent.name}“` });
    if (name === null) return;
    await saveRoom(
      emptyRoom(projectId, {
        name,
        symbol: '◇',
        parentId: parent.id,
        order: childRooms(rooms, parent.id).length,
      }),
    );
    refresh();
  }

  async function renameRoom(room: Room): Promise<void> {
    const name = await askText({ title: 'Raum umbenennen', label: 'Name des Raums', value: room.name, confirm: 'Speichern' });
    if (name === null) return;
    await saveRoom({ ...room, name });
    refresh();
  }

  async function removeRoom(room: Room): Promise<void> {
    const inside = boardsIn(room.id);
    const warning = inside > 0 ? `\n\nDamit gehen auch ${countLabel(inside).toLowerCase()} verloren.` : '';
    if (!confirm(`Raum „${room.name}“ löschen?${warning}`)) return;
    try {
      await deleteRoom(room.id);
      toast('Raum gelöscht');
      refresh();
    } catch (err) {
      // Feste Kategorien wehren sich schon im Datenzugriff, nicht erst hier.
      toast(String(err instanceof Error ? err.message : err));
    }
  }

  /* ----------------------------------------------- Eine Kategorie */

  function categoryCard(category: Room): HTMLElement {
    const children = childRooms(rooms, category.id);

    const childList = h('div', { class: 'room__children' });
    for (const child of children) {
      childList.append(childChip(child));
    }
    childList.append(
      h(
        'button',
        {
          class: 'room__chip room__chip--add',
          attrs: { type: 'button', title: 'Raum anlegen' },
          on: {
            click: (ev) => {
              ev.stopPropagation();
              void addRoom(category);
            },
          },
        },
        h('span', { text: '+ Raum' }),
      ),
    );

    return h(
      'div',
      {
        class: 'room room--system',
        attrs: { role: 'button', tabindex: '0', 'aria-label': category.name },
        on: {
          click: () => navigate(`#/bible/room/${category.id}`),
          keydown: (ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              ev.preventDefault();
              navigate(`#/bible/room/${category.id}`);
            }
          },
        },
      },
      h(
        'div',
        { class: 'room__top' },
        h('span', { class: 'room__symbol', text: category.symbol }),
        // Sichtbares Zeichen, dass diese Kategorie fest ist.
        h('span', { class: 'room__fixed', text: 'fest', attrs: { title: 'Lässt sich nicht löschen oder umbenennen' } }),
      ),
      h('span', { class: 'room__name', text: category.name }),
      h('span', { class: 'room__count', text: countLabel(boardsIn(category.id)) }),
      childList,
    );
  }

  /** Ein eigener Raum innerhalb einer Kategorie. */
  function childChip(room: Room): HTMLElement {
    const chip = h(
      'span',
      { class: 'room__chip' },
      h('button', {
        class: 'room__chip-main',
        text: `${room.name} · ${listCount(room.id)}`,
        attrs: { type: 'button' },
        on: {
          click: (ev) => {
            ev.stopPropagation();
            navigate(`#/bible/room/${room.id}`);
          },
        },
      }),
      button(
        '✎',
        () => void renameRoom(room),
        { class: 'room__chip-tool', title: `„${room.name}“ umbenennen` },
      ),
      button(
        '␡',
        () => void removeRoom(room),
        { class: 'room__chip-tool room__chip-tool--danger', title: `„${room.name}“ löschen` },
      ),
    );
    // Die Knöpfe gehören zum Raum, nicht zum Öffnen der Kategorie.
    chip.addEventListener('click', (ev) => ev.stopPropagation());
    return chip;
  }

  function listCount(roomId: string): string {
    const n = boardsIn(roomId);
    return n === 0 ? 'leer' : String(n);
  }

  const grid = h('div', { class: 'rooms' }, ...categories.map(categoryCard));

  const body = h(
    'div',
    { class: 'bible' },
    h('p', { class: 'bible__lead' },
      'Alles, was die Band wissen muss. Die vier Kategorien sind fest; eigene Räume legst du darin an.',
    ),
    grid,
  );

  return {
    title: 'Band-Bibel',
    subtitle: project.name || 'Ohne Namen',
    body,
  };
}
